import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Gift } from "lucide-react";
import { cn } from "@/lib/utils";

const FLYERS = [
  { src: "/brand/samples/flyer-a.jpg", alt: "Flyer: Is your home still the right fit for you?" },
  { src: "/brand/samples/flyer-b.jpg", alt: "Flyer: When the nest empties, new opportunities take flight" },
  { src: "/brand/samples/flyer-c.jpg", alt: "Flyer: When the nest empties — a brighter, smaller home" },
  { src: "/brand/samples/flyer-d.jpg", alt: "Flyer: Is it time to unlock the value in your home?" },
];
// Where each flyer flies in from before the deck stacks and fans out.
const START = [{ x: -520, y: -240, r: -40 }, { x: -160, y: -460, r: -18 }, { x: 160, y: -460, r: 18 }, { x: 520, y: -240, r: 40 }];
const FLY_MS = 2000;

export function SampleFan({ open = false, size = "md", spread = 1, flyIn = true }) {
  const cw = size === "lg" ? 200 : 132;
  const [settled, setSettled] = useState(!flyIn);
  useEffect(() => {
    if (!flyIn) return undefined;
    const t = setTimeout(() => setSettled(true), FLY_MS);
    return () => clearTimeout(t);
  }, [flyIn]);
  const k = open && settled ? spread : 1;
  const stackX = -cw / 2;
  const stackY = -cw * 0.39;

  return (
    <div className="relative mx-auto" style={{ width: cw * 1.75, height: cw * 1.14 }} aria-hidden="true" data-testid="sample-fan">
      {FLYERS.map((f, i) => {
        const n = i - 1.5;
        const fx = stackX + n * cw * (open ? 0.2 * k : 0.1);
        const fy = stackY + Math.abs(n) * cw * (open ? 0.053 * k : 0.023);
        const fr = n * (open ? 11 * k : 5);
        const fs = open ? 1 : 0.96;
        const s = START[i];
        return (
          <motion.img key={f.src} src={f.src} alt="" loading="lazy" draggable={false}
            className="absolute left-1/2 top-1/2 rounded-[6px] shadow-[0_18px_30px_-12px_rgba(0,0,0,0.75)] ring-1 ring-white/25"
            style={{ width: cw, transformOrigin: "50% 135%", zIndex: i }}
            initial={settled ? false : { x: stackX + s.x, y: stackY + s.y, rotate: s.r, opacity: 0, scale: 0.9 }}
            animate={settled
              ? { x: fx, y: fy, rotate: fr, scale: fs, opacity: 1 }
              : { x: [stackX + s.x, stackX + n * 2, fx], y: [stackY + s.y, stackY + Math.abs(n) * 2, fy], rotate: [s.r, n * 1.5, fr], scale: [0.9, 1, fs], opacity: [0, 1, 1] }}
            transition={settled
              ? { type: "spring", stiffness: 210, damping: 20, delay: i * 0.025 }
              : { duration: 1.45, times: [0, 0.55, 1], ease: ["easeOut", [0.22, 1, 0.36, 1]], delay: 0.1 + i * 0.12 }} />
        );
      })}
    </div>
  );
}

export function FreeSampleOption({ value, onChange }) {
  const [hover, setHover] = useState(false);
  const open = value || hover;
  return (
    <button type="button" role="checkbox" aria-checked={value} onClick={() => onChange(!value)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className={cn("group relative w-full overflow-hidden rounded-3xl border p-5 text-left transition-colors duration-300 sm:p-6", value ? "border-[#96C11F]/70 bg-[#96C11F]/10" : "border-white/10 bg-white/[0.03] hover:border-white/25")}
      data-testid="free-sample-option">
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.28),transparent_65%)]" aria-hidden="true" />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
        <div className="shrink-0 sm:w-[240px]"><SampleFan open={open} /></div>
        <div className="min-w-0 flex-1">
          <p className="eyebrow flex items-center gap-2"><Gift className="h-3.5 w-3.5" /> Free sample pack</p>
          <h4 className="mt-1.5 text-xl font-extrabold tracking-[-0.03em] text-white">See and feel the print before you commit.</h4>
          <p className="mt-1.5 text-[13px] leading-relaxed text-white/60">We'll post you a pack of our homeowner campaign flyers so you can check the stock, finish and feel for yourself.</p>
          <span className="mt-4 inline-flex items-center gap-2.5 text-[13px] font-semibold text-white">
            <span className={cn("grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors", value ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "border-white/30 group-hover:border-white/60")}>
              {value && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
            </span>
            {value ? "Yes please — send me free samples" : "Add free samples to my enquiry"}
          </span>
        </div>
      </div>
    </button>
  );
}
